import * as React from "react";
import {
  Badge,
  Link,
  Popover,
  StatusIndicator,
  Box,
  SpaceBetween,
  StatusIndicatorProps,
  Icon,
  KeyValuePairs,
} from "@amzn/awsui-components-console";
import {
  ArdRevision,
  UslLine,
} from "@amzn/fuse-accounting-central-datastore-client";
import {
  EXTERNAL_SERVICES,
  USL_LINE_KEY_TO_HEADER,
} from "src/components/constants/appConstants";
import {
  DiscrepancyResult,
  DomainDiscrepancyResults,
} from "src/context/flare/flare-comparison-service";
import { UslLineType } from "@amzn/fuse-flare-common";
import { FlareReleaseDomain } from "@amzn/fuse-ard-service-v2-client";
import { useFlareContext } from "src/context/flare/flareContext";
import { useJemGen } from "src/context/jem-gen/jemGenContext";

const { useMemo, useCallback } = React;

// Mapping of USL Line keys to their FRAMES URL paths
const FRAMES_PATH_MAPPING: Partial<{ [K in keyof UslLine]: string }> = {
  companyCode: "/Company/",
  location: "/Location/",
  costCenter: "/Cost%20Center/",
  accountNumber: "/Account/",
  productLine: "/Product/",
  channel: "/Channel/",
  project: "/Project/",
};

const USL_LINE_TYPE_TO_HEADER = {
  DEFERRED_REVENUE: "Deferred Revenue",
  RECEIVABLE: "Receivable",
  REVENUE: "Revenue",
  DEFERRED_REVENUE_RACE: "Deferred Revenue RACE",
  INTERCOMPANY_FROM: "Intercompany From",
  INTERCOMPANY_TO: "Intercompany To",
};

interface UseCaseTableCellProps {
  uslLine: UslLine;
  uslLineKey: keyof UslLine;
  useCaseName: string;
  uslLineIndex: number;
  discrepancy?: DomainDiscrepancyResults;
  uslLineType?: UslLineType;
}

interface RevisionRange {
  startRev: number;
  endRev: number;
  value: string;
}

/**
 * Get field value from a specific revision
 */
const getFieldValueFromRevision = (
  revision: any,
  useCaseName: string,
  uslLineIndex: number,
  fieldKey: keyof UslLine,
): string | undefined => {
  // Find use case by name
  const useCase = revision.ard?.usecases?.find(
    (uc: any) => uc.name === useCaseName,
  );

  // Get USL line by index
  const uslLine = useCase?.uslLines?.[uslLineIndex];

  // Return field value
  return uslLine?.[fieldKey]?.toString();
};

/**
 * Group consecutive revisions with the same value into ranges
 */
const groupRevisionsByValue = (
  revisions: ArdRevision[],
  useCaseName: string,
  uslLineIndex: number,
  fieldKey: keyof UslLine,
): RevisionRange[] => {
  const ranges: RevisionRange[] = [];
  let currentRange: RevisionRange | null = null;

  for (let i = 0; i < revisions.length; i++) {
    const value = getFieldValueFromRevision(
      revisions[i],
      useCaseName,
      uslLineIndex,
      fieldKey,
    );

    // Skip if value is undefined or null
    if (!value) continue;

    // Start new range or continue existing
    if (!currentRange || currentRange.value !== value) {
      if (currentRange) ranges.push(currentRange);
      currentRange = { startRev: i, endRev: i, value };
    } else {
      currentRange.endRev = i;
    }
  }

  if (currentRange) ranges.push(currentRange);
  return ranges;
};

/**
 * Select the 3 revision ranges closest to the current value
 */
const selectClosestRanges = (
  ranges: RevisionRange[],
  currentValue: string,
  maxRanges: number = 5,
): RevisionRange[] => {
  if (ranges.length === 0) return [];

  // Find the range containing the current value
  const currentRangeIndex = ranges.findLastIndex(
    (r) => r.value === currentValue,
  );

  if (currentRangeIndex === -1) {
    // Current value not in history, return most recent ranges
    return ranges.slice(-maxRanges).reverse();
  }

  // Calculate how many ranges to take before and after
  const before = Math.floor((maxRanges - 1) / 2);
  const after = Math.ceil((maxRanges - 1) / 2);

  const startIdx = Math.max(0, currentRangeIndex - before);
  const endIdx = Math.min(ranges.length, currentRangeIndex + after + 1);

  // Return in reverse order (newest to oldest)
  return ranges.slice(startIdx, endIdx).reverse();
};

const getStatusType = (
  discrepancy: DiscrepancyResult,
  loading: boolean,
): StatusIndicatorProps.Type => {
  switch (discrepancy.status) {
    case "match":
      return "success";
    case "mismatch":
      return "error";
    case "missing":
      if (loading) {
        return "loading";
      }
      return "warning";
  }
};

/**
 * Enhanced UseCaseTableCell component that renders a table cell for USL Line data
 * with discrepancy indicators.
 *
 * @param uslLine - The USL Line data object
 * @param uslLineKey - The key of the USL Line field to display
 * @param discrepancy - Optional discrepancy information for this field
 * @returns A React component displaying the cell content with discrepancy indicators
 */
export function UseCaseTableCell({
  uslLine,
  uslLineKey,
  useCaseName,
  uslLineIndex,
  discrepancy,
  uslLineType,
}: UseCaseTableCellProps) {
  const { loadingComparison } = useFlareContext();
  const { selectedArdDetailed, coaDescriptions } = useJemGen();
  const value = uslLine[uslLineKey];

  // If no value exists, display "N/A"
  if (!value) {
    return <span>N/A</span>;
  }

  const getFramesLink = useCallback(
    (uslLineKey: keyof UslLine, value: string) => {
      const framesUrl =
        EXTERNAL_SERVICES.FRAMES + FRAMES_PATH_MAPPING[uslLineKey]! + value;

      return (
        <Link external variant="primary" href={framesUrl}>
          {value}
        </Link>
      );
    },
    [],
  );

  const getFlareConfigLink = useCallback(
    (namedMappingKey?: string, domain?: FlareReleaseDomain) => {
      if (!namedMappingKey) return null;
      const style = {
        display: "inline-block",
        width: "140px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textDecoration: "inherit",
        verticalAlign: "bottom",
      } as const;
      const targetDomain = domain || FlareReleaseDomain.PROD;
      const flareUrl = `${EXTERNAL_SERVICES.FLARE}/${targetDomain}?search_text=${namedMappingKey}`;
      return (
        <Link external variant="primary" href={flareUrl}>
          <span style={style}>{namedMappingKey}</span>
        </Link>
      );
    },
    [],
  );

  const popoverTitle = useMemo(() => {
    return (
      USL_LINE_KEY_TO_HEADER[uslLineKey] +
      (uslLineType ? " -  " + USL_LINE_TYPE_TO_HEADER[uslLineType] : "")
    );
  }, [uslLineKey, uslLineType]);

  // Calculate revision ranges
  const revisionRanges = useMemo(() => {
    if (
      !selectedArdDetailed?.revisions ||
      selectedArdDetailed.revisions.length <= 1 ||
      !value
    ) {
      return [];
    }

    const allRanges = groupRevisionsByValue(
      selectedArdDetailed.revisions,
      useCaseName,
      uslLineIndex,
      uslLineKey,
    );

    return selectClosestRanges(allRanges, value, 5);
  }, [selectedArdDetailed, useCaseName, uslLineIndex, uslLineKey, value]);

  // Helper function to get COA description for a segment value
  const getCoaDescription = useCallback(
    (segmentKey: keyof UslLine, segmentValue: string) => {
      if (segmentKey in FRAMES_PATH_MAPPING && segmentValue) {
        return coaDescriptions.get(`${segmentKey}:${segmentValue}`);
      }
      return undefined;
    },
    [coaDescriptions],
  );

  const popoverContent = useMemo(() => {
    const keyValuePairItems = [
      {
        label: "ARD value",
        value: (
          <SpaceBetween size="m">
            <Box margin="xxs">
              <Box variant="awsui-key-label">Current value</Box>
              <SpaceBetween size="xxs" direction="vertical">
                {getFramesLink(uslLineKey, value)}
                {getCoaDescription(uslLineKey, value) && (
                  <Box color="text-body-secondary">
                    {getCoaDescription(uslLineKey, value)}
                  </Box>
                )}
              </SpaceBetween>
            </Box>
            <Box margin="xxs">
              <Box variant="awsui-key-label">Revisions</Box>
              <SpaceBetween size="xxs" direction="vertical">
                {revisionRanges.length === 0 ? (
                  <span>No historical revisions</span>
                ) : (
                  revisionRanges.map((range, idx) => {
                    const label =
                      range.startRev === range.endRev
                        ? `Rev ${range.startRev}`
                        : `Revs ${range.startRev}-${range.endRev}`;

                    return (
                      <Box key={idx}>
                        <SpaceBetween direction="horizontal" size="xs">
                          <span>{label}:</span>
                          {uslLineKey in FRAMES_PATH_MAPPING ? (
                            getFramesLink(uslLineKey, range.value)
                          ) : (
                            <span>{range.value}</span>
                          )}
                        </SpaceBetween>
                      </Box>
                    );
                  })
                )}
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        ),
      },
    ];

    if (discrepancy) {
      keyValuePairItems.push({
        label: "Flare value",
        value: (
          <SpaceBetween size="m">
            {[
              FlareReleaseDomain.PROD,
              FlareReleaseDomain.UAT,
              FlareReleaseDomain.DEVO,
            ].map((domain) => {
              const flareValue = discrepancy[domain].flareValue;
              const flareCoaDescription = flareValue
                ? getCoaDescription(uslLineKey, flareValue)
                : undefined;

              return (
                <Box margin="xxs" key={domain}>
                  <Box variant="awsui-key-label">{domain}</Box>
                  <SpaceBetween size="xxs" direction="vertical">
                    <SpaceBetween size="xxs" direction="horizontal">
                      {flareValue ? (
                        getFramesLink(uslLineKey, flareValue)
                      ) : (
                        <span>N/A</span>
                      )}
                      <StatusIndicator
                        type={getStatusType(
                          discrepancy[domain],
                          loadingComparison,
                        )}
                      />
                      {discrepancy[domain].namedMappingKey && (
                        <Box>
                          {getFlareConfigLink(
                            discrepancy[domain].namedMappingKey,
                            domain,
                          )}
                        </Box>
                      )}
                    </SpaceBetween>
                    {flareCoaDescription && (
                      <Box color="text-body-secondary">
                        {flareCoaDescription}
                      </Box>
                    )}
                  </SpaceBetween>
                </Box>
              );
            })}
          </SpaceBetween>
        ),
      });
    }

    return <KeyValuePairs columns={2} items={keyValuePairItems} />;
  }, [
    discrepancy,
    getFramesLink,
    getFlareConfigLink,
    getCoaDescription,
    uslLineKey,
    value,
    loadingComparison,
    revisionRanges,
  ]);

  const cellContent = useMemo(() => {
    if (uslLineKey === "debitOrCredit") {
      const isDebit = value.toLowerCase() === "debit";
      return <Badge color={isDebit ? "green" : "red"}>{value}</Badge>;
    }

    if (!(uslLineKey in FRAMES_PATH_MAPPING)) {
      return <span>{value}</span>;
    }

    return (
      <Popover
        // fixedWidth
        renderWithPortal
        position="top"
        size="large"
        triggerType="text"
        dismissButton={false}
        header={popoverTitle}
        content={popoverContent}
      >
        <SpaceBetween direction="horizontal" size="xs">
          {value}
          {discrepancy && (
            <StatusIndicator
              type={getStatusType(discrepancy.prod, loadingComparison)}
              iconAriaLabel={`Flare value ${discrepancy.prod.status}`}
            />
          )}
        </SpaceBetween>
      </Popover>
    );
  }, [
    uslLineKey,
    value,
    loadingComparison,
    discrepancy,
    popoverContent,
    getStatusType,
    popoverTitle,
  ]);

  return (
    <SpaceBetween size="xs" direction="horizontal">
      {cellContent}
    </SpaceBetween>
  );
}
